import AdyenCheckout from "@adyen/adyen-web"
import "@adyen/adyen-web/dist/adyen.css"
import { useLocation } from "@reach/router"
import React from "react"
import { useCallback, useEffect, useRef, useState } from "react"

import { useQueryClient } from "@trueskin-web/apis"

import { usePaymentContext } from "../../payment.context"

import { adyenCheckoutConfiguration, brandsDE } from "./adyen.configuration"

export const useAdyen = ({ paymentMethodType }) => {
  const { handlePurchase, onError, order } = usePaymentContext()
  const { amountInCents, currency, orderId, v2CustomerId, accountId } = order

  const mountRef = useRef(null)
  const checkoutRef = useRef(null)

  const queryClient = useQueryClient()
  const { href } = useLocation()

  const [error, setError] = useState()
  const [isConfirmChargeLoading, setIsConfirmChargeLoading] = useState(false)

  const handleError = useCallback(
    (error) => {
      const { data: { refusalReasonCode } = {} } = error
      setError(refusalReasonCode)

      onError?.({ paymentMethodType, order, refusalReasonCode })
    },
    [onError, paymentMethodType, order, setError]
  )

  const handleSubmit = useCallback(
    (response, component) => {
      // TODO: in the future we should switch to only accountId
      const {
        paymentMethod,
        storePaymentMethod,
        browserInfo,
        origin,
        customerId,
      } = response.data
      setError(null)

      // TODO: think about how to unify the purchase contracts, which includes changes in v2
      handlePurchase({
        amount: amountInCents,
        currency,
        orderId,
        paymentMethod,
        storePaymentMethod,
        provider: "adyen",
        redirectUrl: `${href}?gateway=adyen`,
        browserInfo,
        origin,
        customerId,
        v2CustomerId,
        accountId,
      })
        .then((purchaseResponse) => {
          if (purchaseResponse.action) {
            queryClient.setQueryData("confirmCharge", purchaseResponse)
            component.handleAction(purchaseResponse.action)
          }
        })
        .catch(component.handleError)
        .finally(() => {
          component.setStatus("ready")
        })
    },
    [
      amountInCents,
      currency,
      orderId,
      v2CustomerId,
      accountId,
      href,
      handlePurchase,
      queryClient,
    ]
  )

  const handleAdditionalDetails = useCallback(
    (response, component) => {
      setIsConfirmChargeLoading(true)
      const purchaseResponse = queryClient.getQueryData("confirmCharge")
      const { details, paymentData } = response.data

      handlePurchase({
        provider: "confirm_charge",
        gateway: "adyen",
        actionDetails: paymentData,
        actionData: details,
        txnId: purchaseResponse?.action?.v2TxnId,
        orderId,
      })
        .then(() => {
          component.setStatus("ready")
        })
        .catch((error) => {
          component.handleError(error)
          initAdyenCheckout()
        })
        .finally(() => {
          setIsConfirmChargeLoading(false)
        })
    },
    [handlePurchase, orderId, queryClient] // TODO: there's a chicken and egg problem here with initAdyenCheckout
  )

  const initAdyenCheckout = useCallback(async () => {
    const updateAdyen = async (data) => {
      await checkoutRef.current.update(data)
    }

    if (checkoutRef.current && checkoutRef.current.update) {
      // Force adyen to correctly update itself after price changes
      await updateAdyen({
        ...checkoutRef.current,
        amount: { value: amountInCents, currency },
      })
      return
    }

    const checkout = await AdyenCheckout({
      amount: { value: amountInCents, currency },
      // paymentMethodsResponse: {},
      ...adyenCheckoutConfiguration,
      onError: handleError,
      onSubmit: handleSubmit,
      onAdditionalDetails: handleAdditionalDetails,
    })

    checkoutRef.current = checkout

    if (mountRef.current) {
      switch (paymentMethodType) {
        case "card":
          checkout
            .create(paymentMethodType, {
              brandsDE,
            })
            .mount(mountRef.current)
          break
        default:
          checkout.create(paymentMethodType).mount(mountRef.current)
      }
    }

    return null
  }, [
    mountRef,
    paymentMethodType,
    amountInCents,
    currency,
    handleError,
    handleSubmit,
    handleAdditionalDetails,
  ])

  useEffect(() => {
    const initiateAdyenCheckout = async () => {
      return await initAdyenCheckout()
    }

    initiateAdyenCheckout().catch(console.error)
  }, [initAdyenCheckout])

  return React.useMemo(
    () => ({ mountRef, error, isConfirmChargeLoading }),
    [mountRef, error, isConfirmChargeLoading]
  )
}
